import { BOCA_CANVAS, LETTER_CANVAS } from './layout.js';
import type { TicketDesign } from './layout.js';

/**
 * Designs to start from (D-89).
 *
 * Nobody should face an empty canvas to print a ticket that works. These are
 * complete, publishable designs — they pass validation, they carry a scannable
 * code, and they behave on every preview scenario — so a venue can print
 * tickets on day one and change them afterward.
 */

const text = (id: string, name: string, x: number, y: number, w: number, h: number, value: string, size: number, extra: Record<string, unknown> = {}) => ({
  id,
  kind: 'TEXT' as const,
  name,
  x_mils: x,
  y_mils: y,
  width_mils: w,
  height_mils: h,
  text: value,
  font: 'SANS' as const,
  size_pt: size,
  weight: 'NORMAL' as const,
  align: 'LEFT' as const,
  ...extra,
});

const field = (id: string, name: string, key: string, x: number, y: number, w: number, h: number, size: number, extra: Record<string, unknown> = {}) => ({
  id,
  kind: 'FIELD' as const,
  name,
  field: key,
  x_mils: x,
  y_mils: y,
  width_mils: w,
  height_mils: h,
  font: 'SANS' as const,
  size_pt: size,
  weight: 'NORMAL' as const,
  align: 'LEFT' as const,
  ...extra,
});

/** A PDF ticket for printing at home: the show, the seat, the code. */
export const STARTER_PDF: TicketDesign = {
  schema_version: 1,
  canvas: LETTER_CANVAS,
  elements: [
    { id: 'rule-top', kind: 'LINE', name: 'Rule', x_mils: 500, y_mils: 1450, width_mils: 7500, height_mils: 10, thickness_mils: 10 },
    field('event-title', 'Event title', 'event.title', 500, 600, 5600, 500, 22, { weight: 'BOLD', min_size_pt: 14, max_lines: 2 }),
    field('event-subtitle', 'Subtitle', 'event.subtitle', 500, 1120, 5600, 260, 12, { color_hex: '#5b6270', visible_when: { field: 'event.subtitle', op: 'EXISTS' } }),
    field('order-number', 'Order', 'order.number', 6300, 620, 1700, 200, 10, { align: 'RIGHT', prefix: 'Order ' }),

    field('event-date', 'Date', 'event.date', 500, 1600, 3200, 260, 13, { weight: 'BOLD' }),
    field('event-time', 'Time', 'event.time', 500, 1880, 3200, 240, 12),
    field('doors', 'Doors', 'event.doors', 500, 2140, 3200, 220, 10, { prefix: 'Doors ', color_hex: '#5b6270', visible_when: { field: 'event.doors', op: 'EXISTS' } }),
    field('venue', 'Venue', 'venue.name', 3900, 1600, 4100, 260, 13, { weight: 'BOLD' }),
    field('venue-address', 'Address', 'venue.address', 3900, 1880, 4100, 220, 10, { color_hex: '#5b6270' }),
    field('venue-city', 'City', 'venue.city_region', 3900, 2100, 4100, 220, 10, { color_hex: '#5b6270' }),

    // The entry time only exists for a timed ticket, and when it does it is the thing they need (D-86).
    {
      id: 'entry-box',
      kind: 'BOX',
      name: 'Entry time panel',
      x_mils: 500,
      y_mils: 2500,
      width_mils: 4600,
      height_mils: 900,
      fill_hex: '#f1f5f9',
      border_hex: '#cbd5e1',
      border_width_mils: 10,
      corner_radius_mils: 60,
      visible_when: { field: 'ticket.slot_time', op: 'EXISTS' },
    },
    text('entry-label', 'Entry label', 620, 2620, 2000, 200, 'ENTRY TIME', 9, { color_hex: '#5b6270', visible_when: { field: 'ticket.slot_time', op: 'EXISTS' } }),
    field('entry-time', 'Entry time', 'ticket.slot_time', 620, 2820, 2400, 420, 26, { weight: 'BOLD', visible_when: { field: 'ticket.slot_time', op: 'EXISTS' } }),
    field('entry-note', 'Arrival note', 'ticket.arrival_note', 620, 3240, 4360, 220, 8, { color_hex: '#5b6270', max_lines: 2, min_size_pt: 6, visible_when: { field: 'ticket.arrival_note', op: 'EXISTS' } }),

    text('seat-label', 'Seat label', 500, 3600, 2400, 200, 'SEAT', 9, { color_hex: '#5b6270' }),
    field('seat', 'Seat', 'ticket.seat_label', 500, 3800, 4600, 420, 18, { weight: 'BOLD', min_size_pt: 11, max_lines: 2 }),
    text('type-label', 'Type label', 500, 4300, 2400, 200, 'TICKET', 9, { color_hex: '#5b6270' }),
    field('type', 'Ticket type', 'ticket.type', 500, 4500, 3000, 300, 14, { min_size_pt: 9 }),
    field('price', 'Price', 'price.label', 3600, 4500, 1500, 300, 14, { align: 'RIGHT' }),
    field('holder', 'Holder', 'ticket.holder_name', 500, 4850, 4600, 240, 11, { prefix: 'For ', visible_when: { field: 'ticket.holder_name', op: 'EXISTS' } }),
    field('comp-reason', 'Comp reason', 'admission.comp_reason', 500, 5100, 4600, 240, 10, { prefix: 'Complimentary — ', visible_when: { field: 'admission.comp_reason', op: 'EXISTS' } }),

    { id: 'qr', kind: 'QR', name: 'Admission code', x_mils: 5600, y_mils: 2600, width_mils: 2400, height_mils: 2400, source: 'CREDENTIAL', error_correction: 'M', quiet_zone_mils: 120 },
    // Clear of the QR's quiet zone: 2600 + 2400 + 120 = 5120.
    field('short-code', 'Short code', 'ticket.short_code', 5600, 5150, 2400, 260, 12, { align: 'CENTER', font: 'MONO' }),

    field('reissue', 'Reissue note', 'admission.reissue_note', 500, 5500, 7500, 240, 10, { weight: 'BOLD', visible_when: { field: 'admission.reissue_note', op: 'EXISTS' } }),
    { id: 'rule-bottom', kind: 'LINE', name: 'Rule', x_mils: 500, y_mils: 5900, width_mils: 7500, height_mils: 10, thickness_mils: 10 },
    field('terms', 'Terms', 'admission.terms', 500, 6000, 7500, 700, 8, { color_hex: '#5b6270', max_lines: 4, min_size_pt: 6 }),
    field('sponsor-line', 'Sponsor line', 'sponsor.line', 500, 6750, 7500, 240, 10, { visible_when: { field: 'sponsor.line', op: 'EXISTS' } }),
  ],
};

/** A BOCA ticket on 2" × 5.5" stock, landscape, with a stub on the right. */
export const STARTER_BOCA: TicketDesign = {
  schema_version: 1,
  canvas: BOCA_CANVAS,
  elements: [
    field('event-title', 'Event title', 'event.title', 120, 120, 3600, 320, 14, { weight: 'BOLD', min_size_pt: 8, max_lines: 1 }),
    field('event-date', 'Date', 'event.date', 120, 450, 2200, 220, 9),
    field('event-time', 'Time', 'event.time', 2300, 450, 1400, 220, 9),
    field('venue', 'Venue', 'venue.name', 120, 680, 3600, 200, 8, { min_size_pt: 6, max_lines: 1 }),
    field('seat', 'Seat', 'ticket.seat_label', 120, 920, 3600, 300, 12, { weight: 'BOLD', min_size_pt: 8, max_lines: 1 }),
    field('entry-time', 'Entry time', 'ticket.slot_time', 120, 1220, 1800, 280, 12, { weight: 'BOLD', prefix: 'ENTRY ', visible_when: { field: 'ticket.slot_time', op: 'EXISTS' } }),
    field('type', 'Ticket type', 'ticket.type', 1950, 1250, 1100, 200, 8, { min_size_pt: 6 }),
    field('price', 'Price', 'price.label', 3050, 1250, 650, 200, 8, { align: 'RIGHT' }),
    field('terms', 'Terms', 'admission.terms', 120, 1560, 3600, 200, 6, { max_lines: 2, min_size_pt: 5 }),

    { id: 'stub-rule', kind: 'LINE', name: 'Stub perforation', x_mils: 4000, y_mils: 120, width_mils: 10, height_mils: 1760, thickness_mils: 10, dashed: true },
    { id: 'qr', kind: 'QR', name: 'Admission code', x_mils: 4120, y_mils: 200, width_mils: 1100, height_mils: 1100, source: 'CREDENTIAL', error_correction: 'M', quiet_zone_mils: 60 },
    field('short-code', 'Short code', 'ticket.short_code', 4120, 1380, 1200, 200, 8, { align: 'CENTER', font: 'MONO' }),
    field('stub-seat', 'Stub seat', 'ticket.seat_label', 4120, 1580, 1200, 180, 6, { align: 'CENTER', min_size_pt: 5, max_lines: 1 }),
  ],
};

export const starterDesign = (kind: 'PDF' | 'BOCA'): TicketDesign => (kind === 'BOCA' ? STARTER_BOCA : STARTER_PDF);
